/**
 * 由当前路由生成 vue-meta 的 metaInfo。
 *
 * 挂在 App.vue 根组件上，随 $route 变化自动重算，所以各页面组件不需要各自声明
 * metaInfo —— 只要在 seo.js 的 pages 里有一条记录就够了。
 */
import { buildJsonLd } from './structured-data'
import {
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  OG_LOCALE,
  HREFLANG,
  LOCALES,
  canonicalUrl,
  findPage,
  stripLocale
} from './seo'

const SITE_NAME = 'Design Mirror'

// pages 里没有的路由（比如 404）用这一份兜底
const FALLBACK = {
  zh: {
    title: 'Design Mirror - 设计稿实时预览',
    description: '设计稿实时预览工具，在手机、平板上实时查看设计软件中的设计图。'
  },
  en: {
    title: 'Design Mirror - Live Design Preview',
    description: 'Preview your designs live on phones and tablets, straight from your design tool.'
  }
}

export function buildMetaInfo(routePath, pricePlans) {
  const { path, locale } = stripLocale(routePath)
  const page = findPage(path)
  const copy = (page && page[locale]) || FALLBACK[locale]

  const title = copy.title || FALLBACK[locale].title
  const description = copy.description || FALLBACK[locale].description
  const canonical = canonicalUrl(path, locale)
  const ogImage = SITE_ORIGIN + DEFAULT_OG_IMAGE

  // pages 里没有的路由一律不索引，避免 404 之类被收录
  const indexable = page ? page.index !== false : false

  const meta = [
    { vmid: 'description', name: 'description', content: description },
    // Open Graph —— 微信、Facebook、Slack 等抓这一组
    { vmid: 'og:type', property: 'og:type', content: 'website' },
    { vmid: 'og:site_name', property: 'og:site_name', content: SITE_NAME },
    { vmid: 'og:title', property: 'og:title', content: title },
    { vmid: 'og:description', property: 'og:description', content: description },
    { vmid: 'og:url', property: 'og:url', content: canonical },
    { vmid: 'og:image', property: 'og:image', content: ogImage },
    { vmid: 'og:image:width', property: 'og:image:width', content: '1200' },
    { vmid: 'og:image:height', property: 'og:image:height', content: '630' },
    { vmid: 'og:locale', property: 'og:locale', content: OG_LOCALE[locale] },
    // Twitter Card
    { vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
    { vmid: 'twitter:title', name: 'twitter:title', content: title },
    { vmid: 'twitter:description', name: 'twitter:description', content: description },
    { vmid: 'twitter:image', name: 'twitter:image', content: ogImage }
  ]

  if (!indexable) {
    meta.push({ vmid: 'robots', name: 'robots', content: 'noindex, nofollow' })
  }

  const link = [{ vmid: 'canonical', rel: 'canonical', href: canonical }]

  // 双语互指。只有可索引的页面才值得声明 hreflang。
  if (indexable) {
    LOCALES.forEach(l => {
      link.push({
        vmid: `alternate-${l}`,
        rel: 'alternate',
        hreflang: HREFLANG[l],
        href: canonicalUrl(path, l)
      })
    })
    link.push({
      vmid: 'alternate-default',
      rel: 'alternate',
      hreflang: 'x-default',
      href: canonicalUrl(path, 'zh')
    })
  }

  const info = {
    title,
    htmlAttrs: { lang: locale === 'zh' ? 'zh-CN' : 'en' },
    meta,
    link
  }

  const jsonLd = buildJsonLd(routePath, pricePlans)
  if (jsonLd) {
    info.script = [
      { vmid: 'ld-json', type: 'application/ld+json', innerHTML: JSON.stringify(jsonLd) }
    ]
    // vue-meta 默认会转义 innerHTML，JSON-LD 需要原样输出
    info.__dangerouslyDisableSanitizersByTagID = { 'ld-json': ['innerHTML'] }
  }

  return info
}
